import { useWebAdapter } from "../../../web-adapter";
import type { ReactNode } from "react";
import { FieldErrors, UseFormRegister } from "react-hook-form";
import { OfficeLogoImage } from "../../../components/branding/office-logo-image";
import { Input } from "../../../components/ui/input";
import { Select } from "../../../components/ui/select";
import { SafeDateInput } from "./date-of-birth-input";
import { ScrollableFormShell } from "./scrollable-form-shell";
import { parseRbimDisplayCodes } from "../lib/rbim-display-codes";
import type { AddNewDataFormValues } from "../lib/schema";
import { interviewResultOptions } from "../lib/schema";
import { cn } from "../../../lib/utils";

type RbimFrontPageStepProps = {
  register: UseFormRegister<AddNewDataFormValues>;
  values: AddNewDataFormValues;
  errors: FieldErrors<AddNewDataFormValues>;
  locationLocked: boolean;
  onBarangayChange: (id: string) => void;
  barangayDisabled: boolean;
};

const compactInput =
  "h-9 rounded-none border-slate-400 bg-white px-2 text-sm font-medium shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

const tableInput =
  "h-8 rounded-none border-0 bg-white px-2 text-sm shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimFrontPageStep({ register, values, errors, locationLocked, onBarangayChange, barangayDisabled }: RbimFrontPageStepProps) {
  const web = useWebAdapter();
  const householdErrors = errors.household;
  const lockedLocationInputClass = locationLocked ? "bg-white text-slate-900" : undefined;
  const rbimDisplayCodes = parseRbimDisplayCodes(values.household.barangayCode);
  const familyRegistration = register("household.family");

  const normalizeFamilyInput = (value: string) => {
    const letter = value.trim().toUpperCase().slice(0, 1);
    return /^[A-J]$/.test(letter) ? letter : "A";
  };

  return (
    <ScrollableFormShell
      className="bg-white"
      contentClassName="bg-white"
      minWidthClassName="min-w-0"
      scrollerClassName="overflow-x-hidden"
    >
      <div className="grid min-w-0 gap-4 border-b-8 border-emerald-950 bg-white p-4 lg:grid-cols-[160px_minmax(0,1fr)] xl:grid-cols-[170px_minmax(0,1fr)_minmax(280px,320px)] min-[1600px]:grid-cols-[210px_minmax(0,1fr)_330px]">
        <div className="flex items-center justify-center">
          <OfficeLogoImage
            alt="Office logo"
            className="size-24 rounded-full object-contain min-[1600px]:size-28"
          />
        </div>

        <div className="flex min-w-0 items-center justify-center text-center">
          <h2 className="max-w-3xl text-xl font-extrabold leading-tight tracking-normal text-slate-950 min-[1600px]:max-w-lg min-[1600px]:text-2xl">
            Baseline Census for the Establishment of Registry of Barangay Inhabitants & Migrants
            (RBIM)
          </h2>
        </div>

        <div className="min-w-0 space-y-3 lg:col-span-2 xl:col-span-1">
          <label className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-3 text-lg font-semibold text-slate-950 min-[1600px]:text-xl">
            <span>No.</span>
            <Input
              aria-label="Household form number"
              className={cn(compactInput, "h-12 bg-white text-center text-lg tracking-[0.45em]")}
              maxLength={7}
              placeholder="0000000"
              {...register("household.householdCode")}
            />
          </label>

          <label className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-3 text-lg font-semibold text-slate-950 min-[1600px]:text-xl">
            <span>Family</span>
            <Input
              aria-label="Family"
              autoCapitalize="characters"
              className={cn(compactInput, "h-12 w-16 justify-self-start bg-white text-center text-lg uppercase")}
              maxLength={1}
              pattern="[A-Ja-j]"
              {...familyRegistration}
              onBlur={(event) => {
                event.target.value = normalizeFamilyInput(event.target.value);
                void familyRegistration.onBlur(event);
              }}
              onChange={(event) => {
                event.target.value = normalizeFamilyInput(event.target.value);
                void familyRegistration.onChange(event);
              }}
            />
          </label>

          <div className="grid gap-2 min-[1600px]:grid-cols-2">
            <label className="flex min-h-12 items-center gap-2 border border-slate-500 bg-white px-3 text-sm font-semibold">
              <input
                type="radio"
                value="Household"
                className="size-4 accent-emerald-800"
                {...register("household.householdType")}
              />
              Household
            </label>
            <label className="flex min-h-12 items-center gap-2 border border-slate-500 bg-white px-3 text-sm font-semibold">
              <input
                type="radio"
                value="Institutional Living Quarter"
                className="size-4 accent-emerald-800"
                {...register("household.householdType")}
              />
              Institutional Living Quarter
            </label>
          </div>
          {householdErrors?.householdCode?.message && (
            <p className="text-xs font-semibold text-destructive">{householdErrors.householdCode.message}</p>
          )}
          {householdErrors?.family?.message && (
            <p className="text-xs font-semibold text-destructive">{householdErrors.family.message}</p>
          )}
        </div>
      </div>

      <SectionTitle>A. Identification</SectionTitle>
      <div className="grid min-w-0 gap-x-6 gap-y-5 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] xl:grid-cols-[minmax(0,1fr)_minmax(300px,390px)] min-[1600px]:grid-cols-[minmax(0,1fr)_430px] min-[1600px]:gap-x-8 min-[1600px]:p-5">
        <div className="min-w-0 space-y-3">
          <LocationRow
            label="Province"
            codeLabel="Province/HUC RBIM display code"
            codeDisplayValue={rbimDisplayCodes.provinceOrHucCode}
            error={householdErrors?.province?.message}
            codeRegister={register("household.provinceCode")}
            locationLocked={locationLocked}
          >
            <Input
              autoUppercase
              className={cn(compactInput, lockedLocationInputClass)}
              readOnly={locationLocked}
              {...register("household.province")}
            />
          </LocationRow>

          <LocationRow
            label="City/Municipality"
            codeLabel="City/Municipality RBIM display code"
            codeDisplayValue={rbimDisplayCodes.municipalityCityCode}
            error={householdErrors?.municipality?.message}
            codeRegister={register("household.municipalityCode")}
            locationLocked={locationLocked}
          >
            <Input
              autoUppercase
              className={cn(compactInput, lockedLocationInputClass)}
              readOnly={locationLocked}
              {...register("household.municipality")}
            />
          </LocationRow>

          <LocationRow
            label="Barangay"
            codeLabel="Barangay RBIM display code"
            codeDisplayValue={rbimDisplayCodes.barangayCode}
            error={householdErrors?.barangay?.message}
            codeRegister={register("household.barangayCode")}
            locationLocked={locationLocked}
          >
            <Select
              aria-label="Select Barangay"
              className={cn(compactInput, lockedLocationInputClass)}
              value={web.barangayId}
              disabled={barangayDisabled || web.geographyLoading || !web.barangays.length}
              onChange={(event) => onBarangayChange(event.target.value)}
            >
              <option value="">{web.geographyLoading ? "Loading Barangays..." : "Select Barangay"}</option>
              {web.barangays.map((barangay) => <option key={barangay.id} value={barangay.id}>{barangay.name}</option>)}
            </Select>
          </LocationRow>

          <label className="grid min-w-0 gap-2 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-center xl:grid-cols-[170px_minmax(0,1fr)]">
            <span className="text-sm font-medium text-slate-900">Address</span>
            <Input
              autoUppercase
              className={compactInput}
              placeholder="House no., street, sitio, block, or landmark"
              {...register("household.address")}
            />
            {householdErrors?.address?.message && (
              <span className="sm:col-start-2 text-xs font-semibold text-destructive">
                {householdErrors.address.message}
              </span>
            )}
          </label>
        </div>

        <div className="min-w-0 space-y-5">
          <LegacyLineField
            label="Name of Respondent"
            error={householdErrors?.respondentName?.message}
            inputProps={register("household.respondentName")}
          />
          <LegacyLineField
            label="Household Head"
            error={householdErrors?.householdHead?.message}
            inputProps={register("household.householdHead")}
          />
          <label className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_96px] sm:items-center min-[1600px]:grid-cols-[minmax(0,1fr)_110px]">
            <span className="text-sm font-medium text-slate-900">Total No. of Household Members</span>
            <Input
              className={cn(compactInput, "bg-white text-center text-lg font-bold")}
              inputMode="numeric"
              maxLength={2}
              placeholder="00"
              {...register("household.totalMembers")}
            />
            {householdErrors?.totalMembers?.message && (
              <span className="sm:col-span-2 text-xs font-semibold text-destructive">
                {householdErrors.totalMembers.message}
              </span>
            )}
          </label>
        </div>
      </div>

      {web.geographyError && <p role="alert" className="px-4 pb-3 text-sm font-semibold text-destructive">{web.geographyError}</p>}
      <SectionTitle>B. Interview Information</SectionTitle>
      <div className="min-w-0 max-w-full overflow-x-auto px-4 py-4 min-[1600px]:px-5">
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead>
            <tr className="bg-white text-slate-950">
              <HeaderCell className="w-[95px]">Visit</HeaderCell>
              <HeaderCell>Date of Visit</HeaderCell>
              <HeaderCell>Time start</HeaderCell>
              <HeaderCell>Time end</HeaderCell>
              <HeaderCell>
                <span className="block">Result</span>
                <span className="text-[11px] font-medium leading-tight">
                  C=Completed<br />CB=Callback<br />R=Refused
                </span>
              </HeaderCell>
              <HeaderCell>Date of Next Visit</HeaderCell>
              <HeaderCell>Name of Interviewer, Initial/Date</HeaderCell>
              <HeaderCell>Name of Supervisor, Initial/Date</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {[0, 1].map((index) => (
              <tr key={index}>
                <BodyCell>
                  <Input className={cn(tableInput, "font-semibold")} readOnly {...register(`interviews.${index}.visit`)} />
                </BodyCell>
                <BodyCell>
                  <SafeDateInput
                    allowFutureDates
                    calendarLabel="Choose date of visit"
                    className={tableInput}
                    placeholder="MM/DD/YYYY"
                    registration={register(`interviews.${index}.dateOfVisit`)}
                    validationMode="optional-date"
                    value={values.interviews[index]?.dateOfVisit}
                  />
                </BodyCell>
                <BodyCell>
                  <Input className={tableInput} type="time" {...register(`interviews.${index}.timeStart`)} />
                </BodyCell>
                <BodyCell>
                  <Input className={tableInput} type="time" {...register(`interviews.${index}.timeEnd`)} />
                </BodyCell>
                <BodyCell>
                  <Select className={tableInput} {...register(`interviews.${index}.result`)}>
                    {interviewResultOptions.map((option) => (
                      <option key={option || "blank"} value={option}>
                        {option || ""}
                      </option>
                    ))}
                  </Select>
                </BodyCell>
                <BodyCell>
                  <SafeDateInput
                    allowFutureDates
                    calendarLabel="Choose date of next visit"
                    className={tableInput}
                    placeholder="MM/DD/YYYY"
                    registration={register(`interviews.${index}.dateOfNextVisit`)}
                    validationMode="optional-date"
                    value={values.interviews[index]?.dateOfNextVisit}
                  />
                </BodyCell>
                <BodyCell>
                  <Input autoUppercase className={tableInput} {...register(`interviews.${index}.interviewer`)} />
                </BodyCell>
                <BodyCell>
                  <Input autoUppercase className={tableInput} {...register(`interviews.${index}.supervisor`)} />
                </BodyCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle>C. Encoding Information</SectionTitle>
      <div className="px-4 py-4 min-[1600px]:px-5">
        <div className="mx-auto min-w-0 max-w-full overflow-x-auto">
          <table className="mx-auto w-full max-w-3xl min-w-[680px] border-collapse text-sm">
            <thead>
              <tr>
                <HeaderCell>Date Encoded</HeaderCell>
                <HeaderCell>Name and Initial of Encoder</HeaderCell>
                <HeaderCell>Name of Supervisor, Initial and Date</HeaderCell>
              </tr>
            </thead>
            <tbody>
              <tr>
                <BodyCell>
                  <SafeDateInput
                    allowFutureDates
                    calendarLabel="Choose date encoded"
                    className={tableInput}
                    placeholder="MM/DD/YYYY"
                    registration={register("encoding.dateEncoded")}
                    validationMode="optional-date"
                    value={values.encoding.dateEncoded}
                  />
                </BodyCell>
                <BodyCell>
                  <Input autoUppercase className={tableInput} {...register("encoding.encoder")} />
                </BodyCell>
                <BodyCell>
                  <Input autoUppercase className={tableInput} {...register("encoding.supervisor")} />
                </BodyCell>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ScrollableFormShell>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="border-y-4 border-emerald-950 bg-white py-2 text-center text-xl font-extrabold uppercase tracking-normal text-slate-950">
      {children}
    </div>
  );
}

function HeaderCell({ children, className }: { children: ReactNode; className?: (string) | undefined }) {
  return (
    <th className={cn("border border-slate-500 px-2 py-2 align-bottom font-semibold text-slate-950", className)}>
      {children}
    </th>
  );
}

function BodyCell({ children }: { children: ReactNode }) {
  return <td className="border border-slate-500 bg-white p-0 align-middle">{children}</td>;
}

function LocationRow({
  label,
  codeLabel,
  codeDisplayValue,
  codeRegister,
  error,
  locationLocked,
  children,
}: {
  label: string;
  codeLabel: string;
  codeDisplayValue: string;
  codeRegister: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  error?: (string) | undefined;
  locationLocked: boolean;
  children: ReactNode;
}) {
  return (
    <label className="grid min-w-0 gap-2 sm:grid-cols-[150px_minmax(0,1fr)_120px] sm:items-center xl:grid-cols-[170px_minmax(0,1fr)_140px] min-[1600px]:grid-cols-[170px_minmax(0,1fr)_150px]">
      <span className="text-sm font-medium text-slate-900">{label}</span>
      {children}
      <input type="hidden" {...codeRegister} />
      <Input
        aria-label={codeLabel}
        className={cn(
          compactInput,
          "bg-white text-center text-base tracking-[0.12em] min-[1600px]:text-lg min-[1600px]:tracking-[0.18em]",
          locationLocked && "bg-white text-slate-900",
        )}
        readOnly
        value={codeDisplayValue}
      />
      {error && <span className="sm:col-start-2 text-xs font-semibold text-destructive">{error}</span>}
    </label>
  );
}

function LegacyLineField({
  label,
  error,
  inputProps,
}: {
  label: string;
  error?: (string) | undefined;
  inputProps: ReturnType<UseFormRegister<AddNewDataFormValues>>;
}) {
  return (
    <label className="grid min-w-0 gap-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:items-center min-[1600px]:grid-cols-[150px_minmax(0,1fr)]">
      <span className="text-sm font-medium text-slate-900">{label}</span>
      <Input autoUppercase className={compactInput} {...inputProps} />
      {error && <span className="sm:col-start-2 text-xs font-semibold text-destructive">{error}</span>}
    </label>
  );
}
